import React, { useRef, useState, useCallback } from 'react';
import { sanitizeHtml } from '../../lib/sanitizeHtml';
import { removeTrackingPixel } from './mailHubUtils';

/**
 * Affiche le corps HTML d'un email dans une <iframe> isolée.
 *
 * Pourquoi une iframe : le HTML des emails reçus utilise souvent des noms de
 * classe communs (collapse, hidden, table, grid…) qui entrent en collision avec
 * les utilitaires Tailwind de l'application et masquent/déforment le contenu
 * (ex. Bootstrap `.collapse` = `visibility:collapse` en Tailwind). L'iframe place
 * le contenu dans son propre document : aucun style de l'app ne peut l'affecter.
 *
 * Sécurité : sandbox SANS `allow-scripts` (aucun JS de l'email n'est exécuté),
 * et contenu tout de même assaini via DOMPurify.
 */
export const EmailHtmlFrame = ({ html, className = '' }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(220);

  const clean = sanitizeHtml(removeTrackingPixel(html || ''));
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>
    html,body{margin:0;padding:0}
    body{padding:4px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;font-size:14px;line-height:1.6;word-wrap:break-word;overflow-wrap:break-word;overflow-x:hidden}
    img{max-width:100% !important;height:auto}
    table{max-width:100%}
    a{color:#2563eb}
  </style></head><body>${clean}</body></html>`;

  const resize = useCallback(() => {
    const measure = () => {
      try {
        const doc = ref.current?.contentDocument;
        if (doc && doc.body) {
          const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
          if (h > 0) setHeight(h + 8);
        }
      } catch (e) {
        /* origine opaque : on conserve la hauteur par défaut */
      }
    };
    measure();
    // Re-mesure après chargement différé des images
    setTimeout(measure, 300);
    setTimeout(measure, 1200);
  }, []);

  return (
    <iframe
      ref={ref}
      title="email-body"
      onLoad={resize}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className={className}
      style={{ width: '100%', height: `${height}px`, border: 'none', display: 'block' }}
      data-testid="email-html-frame"
    />
  );
};

export default EmailHtmlFrame;
